'use client';

import {
  AnalyticsBrowser,
  type AnalyticsBrowserSettings,
  type InitOptions,
} from '@segment/analytics-next';
import { createContext, useContext, type PropsWithChildren } from 'react';
import { useConstant } from '../../hooks/useConstant';
import { SegmentLive, SegmentMock, type SegmentAbstraction } from './Segment';
import { SEGMENT_API_HOST, SEGMENT_CDN_URL } from './constants';

/**
 * Analytics Context
 */
export interface AnalyticsContextValue {
  deployableName: string;
  deployableVersion: string;
  analytics?: SegmentAbstraction;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

/**
 * Analytics Provider
 */

export interface AnalyticsProviderProps {
  deployableName: string;
  deployableVersion: string;
  writeKey?: string;
  debug?: boolean;
}

export const AnalyticsProvider = ({
  deployableName,
  deployableVersion,
  writeKey,
  debug = false,
  children,
}: PropsWithChildren<AnalyticsProviderProps>) => {
  const analytics = useConstant(() => {
    if (debug) {
      return new SegmentMock();
    }
    if (writeKey) {
      const settings: AnalyticsBrowserSettings = {
        writeKey,
        cdnURL: SEGMENT_CDN_URL,
      };
      const options: InitOptions = {
        integrations: {
          'Segment.io': {
            apiHost: SEGMENT_API_HOST,
            protocol: 'https',
          },
        },
        storage: {
          stores: ['cookie', 'localStorage', 'memory'],
        },
      };
      const browserAnalytics = AnalyticsBrowser.load(settings, options);
      return new SegmentLive(browserAnalytics);
    }
  }, [writeKey, debug]);

  return (
    <AnalyticsContext.Provider value={{ deployableName, deployableVersion, analytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics hook must used within an AnalyticsProvider');
  }
  return context;
};
